import { Image } from '@common/components/Image';
import { FontFamily } from '@common/theming/Fonts';
import { Palette } from '@common/theming/Palette';
import React, { CSSProperties } from 'react';
import { Features, Highlights, Map, TopEmployers } from '.';
import { BRANCH_APP_LINK } from '../../constants';
import { PortfolioAsset } from '../../types';
import { TextBlock } from '../TextBlock';

const Section: React.FC<{ children: React.ReactNode; bg?: string; marginBottom?: CSSProperties['marginBottom'] }> = ({
  bg = Palette.WHITE,
  children,
  marginBottom = 0,
}) => {
  return (
    <div
      style={{
        alignItems: 'center',
        backgroundColor: bg,
        display: 'flex',
        justifyContent: 'center',
        marginBottom,
        width: '100%',
      }}>
      {children}
    </div>
  );
};

const Content: React.FC<{
  children: React.ReactNode;
  bg?: string;
  alignItems?: CSSProperties['alignItems'];
  padding?: CSSProperties['padding'];
}> = ({ bg = Palette.WHITE, children, alignItems = 'center', padding = 30 }) => {
  return (
    <div
      style={{
        alignItems,
        backgroundColor: bg,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        padding,
        textAlign: 'left',
      }}>
      {children}
    </div>
  );
};

type AssetProps = {
  asset: PortfolioAsset;
  showImageGallery: () => void;
};

export const Asset: React.FC<AssetProps> = ({ asset, showImageGallery }) => {
  return (
    <div style={{ backgroundColor: Palette.WHITE, paddingBottom: 10 }}>
      {/* Header */}
      <Section marginBottom={0}>
        <Content>
          <Image image="logo-black" width={100} />
        </Content>
      </Section>

      {/* Hero */}
      <Section bg={Palette.LIGHT_GRAY}>
        <Content bg={Palette.LIGHT_GRAY}>
          <TextBlock fontFamily={FontFamily.DM_SERIF_DISPLAY_REGULAR} size={36} color={Palette.BLACK}>
            {asset.header.title}
          </TextBlock>
          <TextBlock size={16} color={Palette.BLACK} style={{ marginTop: 10 }}>
            {asset.header.locationName}
          </TextBlock>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 30, width: '100%' }}>
            <div
              onClick={() => {
                showImageGallery();
              }}
              style={{
                backgroundImage: `url(${asset.header.headerImage.file.url})`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                cursor: 'pointer',
                height: 251,
                width: '100%',
              }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginRight: asset.images.length > 3 ? 5 : 0,
                  marginTop: 5,
                }}>
                <div style={{ backgroundColor: 'rgba(0,0,0,0.20)', display: 'flex', padding: 5 }}>
                  {asset.images.slice(0, 3).map((image, i) => {
                    return (
                      <div
                        key={`image-preview${i}`}
                        style={{
                          border: `1px solid ${Palette.WHITE}`,
                          marginRight: asset.images.length > 3 ? 5 : 0,
                        }}>
                        <img src={image.file.url} style={{ display: 'flex', height: 32, width: 38 }} />
                      </div>
                    );
                  })}
                  {asset.images.length > 3 ? (
                    <div
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        height: 32,
                        justifyContent: 'center',
                        width: 38,
                      }}>
                      <TextBlock>{`+${asset.images.length - 3}`}</TextBlock>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 30, width: '100%' }}>
            <Highlights highlights={asset.assetHighlights}></Highlights>
          </div>
        </Content>
      </Section>

      {/* About */}
      <Section marginBottom={0}>
        <Content alignItems="flex-start">
          <TextBlock size={24} color={Palette.BLACK}>
            About this asset
          </TextBlock>
          <TextBlock style={{ marginTop: 12 }} size={16} color={Palette.BLACK}>
            {asset.description}
          </TextBlock>
        </Content>
      </Section>

      {/* Features */}
      {asset.assetFeatures && (
        <Section marginBottom={0}>
          <Content alignItems="flex-start">
            <Features asset={asset} />
          </Content>
        </Section>
      )}

      {/* Top Employers */}
      {asset.topEmployers && (
        <Section marginBottom={0}>
          <Content alignItems="flex-start">
            <TopEmployers asset={asset} />
          </Content>
        </Section>
      )}

      {/* Marketing Text  */}
      <Section>
        <Content alignItems={'flex-start'}>
          {asset.marketingText?.map((text, i) => (
            <div key={`marketingText-${i}`}>
              {text.heading && (
                <TextBlock style={{ marginBottom: 20 }} size={24} color={Palette.BLACK}>
                  {text.heading}
                </TextBlock>
              )}
              {text.marketingParagraph && (
                <TextBlock style={{ marginBottom: 20 }} size={16} color={Palette.BLACK}>
                  {text.marketingParagraph}
                </TextBlock>
              )}
              {text.additionalParagraph1 && (
                <TextBlock style={{ marginBottom: 20 }} size={16} color={Palette.BLACK}>
                  {text.additionalParagraph1}
                </TextBlock>
              )}
              {text.additionalParagraph2 && (
                <TextBlock style={{ marginBottom: 20 }} size={16} color={Palette.BLACK}>
                  {text.additionalParagraph2}
                </TextBlock>
              )}
            </div>
          ))}
        </Content>
      </Section>

      <Section>
        <Content>
          <Map asset={asset} />
        </Content>
      </Section>

      {/* CTA */}
      <Section>
        <Content>
          <div
            style={{
              backgroundColor: Palette.LIGHT_GRAY,
              border: `1px solid #E1E1E1`,
              borderRadius: 15,
              paddingTop: 20,
            }}>
            <img
              style={{ height: 200, objectFit: 'none', width: '100%' }}
              src="/assets/gfx/AssetMarketingGraphic.svg"
            />
            <TextBlock
              style={{ marginTop: 0, textAlign: 'center' }}
              color={Palette.BLACK}
              size={20}
              fontFamily={FontFamily.DM_SERIF_DISPLAY_REGULAR}>
              Add this to your portfolio
            </TextBlock>
            <TextBlock
              fontFamily={FontFamily.NEUE_HAAS_DISPLAY_THIN}
              style={{ marginTop: 20, textAlign: 'center' }}
              color={Palette.BLACK}
              size={16}>
              Build your wealth with real estate
            </TextBlock>
            <TextBlock
              fontFamily={FontFamily.NEUE_HAAS_DISPLAY_THIN}
              style={{ textAlign: 'center' }}
              color={Palette.BLACK}
              size={16}>
              investing in a hands-off way
            </TextBlock>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 55,
                marginTop: 36,
                width: '100%',
              }}>
              <button
                onClick={() => {
                  window.open(asset.ctaUrl || BRANCH_APP_LINK, '_blank');
                }}
                style={{
                  alignItems: 'center',
                  backgroundColor: Palette.BLUE,
                  border: 0,
                  borderRadius: 10,
                  display: 'flex',
                  height: 60,
                  justifyContent: 'center',
                  width: 235,
                }}>
                <TextBlock size={16}>Invest Now</TextBlock>
              </button>
            </div>
          </div>
        </Content>
      </Section>
    </div>
  );
};
